import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, MessageCircle, ArrowRight, Sparkles, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { toast } from 'sonner';

export default function AuthPage() {
  const [step, setStep] = useState('phone'); // 'phone' | 'otp' | 'success'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (phoneNumber.length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }

    setIsLoading(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setIsLoading(false);
    setStep('otp');
    toast.success('OTP sent to your phone!');
  };

  const handleVerifyOTP = async () => {
    if (otp.length < 6) {
      toast.error('Please enter complete OTP');
      return;
    }

    setIsLoading(true);
    // Simulate verification
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setIsLoading(false);
    setStep('success');
    toast.success('Login successful!');
  };

  return (
    <div className="page-transition min-h-screen flex items-center justify-center py-12">
      {/* Background */}
      <div className="fixed inset-0 bg-gradient-fabric -z-10" />
      <div className="fixed inset-0 bg-fabric-texture -z-10" />

      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md mx-auto"
        >
          {/* Card */}
          <div className="bg-card/95 backdrop-blur-md rounded-2xl border border-border/50 overflow-hidden shadow-xl">
            {/* Header */}
            <div className="p-6 md:p-8 text-center border-b border-border/30">
              <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-gradient-burgundy border border-silver/20 mb-4">
                <User className="h-7 w-7 text-silver" />
              </div>
              <h1 className="font-display text-2xl text-foreground mb-2">
                Welcome to Fazila Creations
              </h1>
              <p className="text-sm text-muted-foreground font-body">
                Sign in to track orders and save favorites
              </p>
            </div>

            {/* Content */}
            <div className="p-6 md:p-8">
              <AnimatePresence mode="wait">
                {/* Phone Number Step */}
                {step === 'phone' && (
                  <motion.div
                    key="phone"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                  >
                    <form onSubmit={handleSendOTP} className="space-y-6">
                      <div>
                        <label className="block text-sm text-silver-light mb-2">
                          Phone Number
                        </label>
                        <div className="relative">
                          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                          <Input
                            type="tel"
                            variant="luxury"
                            placeholder="Enter your phone number"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                            className="pl-12"
                            maxLength={10}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          We'll send you a one-time password
                        </p>
                      </div>

                      <Button
                        type="submit"
                        variant="ivory"
                        size="lg"
                        className="w-full"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <span className="animate-pulse">Sending OTP...</span>
                        ) : (
                          <>
                            Send OTP
                            <ArrowRight className="h-4 w-4" />
                          </>
                        )}
                      </Button>
                    </form>

                    {/* Divider */}
                    <div className="relative my-8">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-border" />
                      </div>
                      <div className="relative flex justify-center text-xs">
                        <span className="bg-card px-4 text-muted-foreground">or</span>
                      </div>
                    </div>

                    {/* WhatsApp Login */}
                    <a
                      href="https://wa.me/919111841033?text=Assalamu%20Alaikum!%20I%20want%20to%20create%20an%20account."
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button variant="whatsapp" className="w-full">
                        <MessageCircle className="h-5 w-5" />
                        Continue with WhatsApp
                      </Button>
                    </a>
                  </motion.div>
                )}

                {/* OTP Step */}
                {step === 'otp' && (
                  <motion.div
                    key="otp"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="space-y-6"
                  >
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground mb-4">
                        Enter the 6-digit code sent to
                      </p>
                      <p className="font-medium text-silver-light mb-6">
                        +91 {phoneNumber}
                      </p>
                    </div>

                    <div className="flex justify-center">
                      <InputOTP maxLength={6} value={otp} onChange={setOtp}>
                        <InputOTPGroup>
                          <InputOTPSlot index={0} className="bg-muted border-border" />
                          <InputOTPSlot index={1} className="bg-muted border-border" />
                          <InputOTPSlot index={2} className="bg-muted border-border" />
                          <InputOTPSlot index={3} className="bg-muted border-border" />
                          <InputOTPSlot index={4} className="bg-muted border-border" />
                          <InputOTPSlot index={5} className="bg-muted border-border" />
                        </InputOTPGroup>
                      </InputOTP>
                    </div>

                    <Button
                      variant="ivory"
                      size="lg"
                      className="w-full"
                      onClick={handleVerifyOTP}
                      disabled={isLoading || otp.length < 6}
                    >
                      {isLoading ? (
                        <span className="animate-pulse">Verifying...</span>
                      ) : (
                        'Verify OTP'
                      )}
                    </Button>

                    <div className="text-center">
                      <button
                        onClick={() => setStep('phone')}
                        className="text-sm text-muted-foreground hover:text-silver transition-colors"
                      >
                        Change phone number
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* Success Step */}
                {step === 'success' && (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8"
                  >
                    <div className="h-16 w-16 rounded-full bg-success/20 flex items-center justify-center mx-auto mb-6">
                      <Sparkles className="h-8 w-8 text-success" />
                    </div>
                    <h2 className="font-display text-xl text-silver-light mb-3">
                      Welcome!
                    </h2>
                    <p className="text-muted-foreground font-body mb-6">
                      You're now signed in. Start exploring our beautiful collections.
                    </p>
                    <a href="/products">
                      <Button variant="ivory" size="lg">
                        Start Shopping
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </a>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Trust Note */}
          <p className="text-xs text-muted-foreground text-center mt-6 font-body">
            Your information is safe and secure with us.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
